import React, { useEffect, useState } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import EmptyState from '../../components/EmptyState';
import { getDrgHccReview } from '../../services/suiteApi';

export default function DrgHccPage() {
  usePageTitle('DRG / HCC review');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getDrgHccReview();
        if (!cancelled) setData(resp?.data || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const drg = data?.drgFindings || [];
  const hcc = data?.hccFindings || [];

  return (
    <div className="dm-page">
      <header className="dm-page__header">
        <h1 className="dm-page__title">DRG validation & HCC capture</h1>
        <p className="dm-page__lede">
          Two cross-cutting risk-adjustment checks: institutional claims with a
          potential DRG upgrade, and professional encounters where an HCC
          opportunity was missed.
        </p>
      </header>

      {error && <div className="dm-banner dm-banner--error">{error}</div>}
      {loading && <div className="dm-card__empty">Loading...</div>}

      <section className="dm-card">
        <div className="dm-card__title">DRG findings ({drg.length})</div>
        {drg.length === 0 ? (
          <EmptyState
            title="No DRG findings"
            body="DRG validation runs on every institutional claim once you post one."
            ctaLabel="Upload an institutional claim"
            ctaTo="/intake/new"
          />
        ) : (
          <table className="dm-table">
            <thead>
              <tr>
                <th>Finding</th>
                <th>From DRG</th>
                <th>To DRG</th>
                <th>Rationale</th>
              </tr>
            </thead>
            <tbody>
              {drg.map((f, i) => (
                <tr key={i}>
                  <td>{f.finding}</td>
                  <td>{f.from}</td>
                  <td>{f.to}</td>
                  <td>{f.rationale || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="dm-card">
        <div className="dm-card__title">HCC findings ({hcc.length})</div>
        {hcc.length === 0 ? (
          <div className="dm-card__empty">
            HCC capture review surfaces missed risk-adjustment opportunities once
            you have a Medicare Advantage encounter on file.
          </div>
        ) : (
          <table className="dm-table">
            <thead>
              <tr>
                <th>HCC</th>
                <th>Missing dx</th>
                <th>Encounter</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {hcc.map((f, i) => (
                <tr key={i}>
                  <td>{f.hcc || f.hccCode}</td>
                  <td>{f.missing_dx || f.missingDx || '--'}</td>
                  <td>{f.encounterRef || ''}</td>
                  <td>{f.source || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
